import React, { useCallback, useEffect, useState } from 'react';
import { Button, Dropdown, MenuProps, Spin, Tooltip } from 'antd';
import { DownOutlined } from '@ant-design/icons';

import {
  CourseInstanceAttendee,
  DisplayedCourseInstance,
} from '../types/CourseInstance';
import { t } from 'i18next';
import CertificateAPI from '../../certificate/CertificateAPI';
import {
  createCertificateFileName,
  createCertificateZipFileName,
} from '../../certificate/CertificateUtils';
import { CertificateLanguage } from '../../certificate/types/Certificate';
import Certificate from '../../resources/img/certificate.svg';
import { getCertificateDownloadTooltip } from '../utils/certificateUtils';

type DownloadCertificateDropdownProps = {
  courseInstance: DisplayedCourseInstance;
  selectedAttendees: CourseInstanceAttendee[];
  certificateLanguages: CertificateLanguage[];
  loadingCertificateLanguages: boolean;
};

const DownloadCertificateDropdown: React.FC<
  DownloadCertificateDropdownProps
> = ({
  courseInstance,
  selectedAttendees,
  certificateLanguages,
  loadingCertificateLanguages,
}) => {
  const [
    certificateNotDownloadableTooltip,
    setCertificateNotDownloadableTooltip,
  ] = useState<string>();

  const downloadCertificates = useCallback<NonNullable<MenuProps['onClick']>>(
    ({ key }) => {
      if (
        certificateNotDownloadableTooltip ||
        !courseInstance.startsAt ||
        key == '-1'
      ) {
        return;
      }

      if (selectedAttendees.length === 1) {
        const attendee = selectedAttendees[0];
        const fileName = createCertificateFileName(
          attendee.name,
          courseInstance.course.name,
          courseInstance.startsAt,
        );
        if (attendee.id) {
          CertificateAPI.getCertificateForAttendee(
            fileName,
            attendee.id,
            parseInt(key),
          );
        } else if (attendee.elsaId) {
          CertificateAPI.getCertificateForElsaParticipant(
            fileName,
            attendee.elsaId,
            courseInstance.id!,
            parseInt(key),
          );
        } else {
          throw Error("Attendee must either have an 'id' or an 'elsaId'");
        }
      } else {
        const attendeeIds = selectedAttendees
          .map((a) => a.id)
          .filter((id) => !!id);
        const attendeeElsaIds = selectedAttendees
          .map((a) => a.elsaId)
          .filter((id) => !!id);

        if (
          (attendeeIds.length === 0 && attendeeElsaIds.length === 0) ||
          (attendeeIds.length > 0 && attendeeElsaIds.length > 0)
        ) {
          throw Error("Attendees must either have an 'id' or an 'elsaId'");
        }

        const fileName = createCertificateZipFileName(
          courseInstance.course.name,
          courseInstance.startsAt,
        );

        CertificateAPI.getCertificateZip(
          fileName,
          courseInstance.course.id,
          courseInstance.id!,
          parseInt(key),
          attendeeIds.map((id) => id!),
          attendeeElsaIds.map((elsaId) => elsaId!),
        );
      }
    },
    [
      certificateNotDownloadableTooltip,
      courseInstance.course.id,
      courseInstance.course.name,
      courseInstance.id,
      courseInstance.startsAt,
      selectedAttendees,
    ],
  );

  useEffect(() => {
    setCertificateNotDownloadableTooltip(
      getCertificateDownloadTooltip(
        selectedAttendees,
        courseInstance,
        certificateLanguages,
      ),
    );
  }, [certificateLanguages, courseInstance, selectedAttendees]);

  return (
    <Dropdown
      trigger={['click']}
      className={!!certificateNotDownloadableTooltip ? 'bg-gray-100' : ''}
      menu={{
        items: certificateLanguages.map((language) => {
          return {
            label: language.name,
            key: language.id.toString(),
          };
        }),
        onClick: downloadCertificates,
      }}>
      <Tooltip title={certificateNotDownloadableTooltip}>
        <Button
          disabled={!!certificateNotDownloadableTooltip || !courseInstance.id}
          className={`flex border-1 shadow-none rounded h-10 items-center font-medium ${
            !!certificateNotDownloadableTooltip ? '' : 'border-blue-300'
          }`}>
          <img src={Certificate} className="pr-1" />
          {t('components.DownloadCertificateDropdown.downloadCertificates')}
          <div className="flex items-center pl-2">
            {loadingCertificateLanguages ? (
              <Spin size="small" />
            ) : (
              <DownOutlined />
            )}
          </div>
        </Button>
      </Tooltip>
    </Dropdown>
  );
};

export default DownloadCertificateDropdown;
